import { useEffect, useMemo, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { mapKeys, mapValues, pickBy } from "lodash";
import { ExtraLoginParams, ModifiedAvoType } from "@shared/analytics/types";
import { useTheme } from "styled-components";

export const useAvoProvider = <AvoType extends { userLoggedIn: any }>(
  Avo: AvoType,
  onUserIdChange?: (updatedId: string, oldId: string) => void,
  enableLoading?: boolean,
  userSystemDarkModeOn?: boolean,
  isTelegramWebApp?: boolean
) => {
  const theme = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  const [initializedUserId, setInitializedUserId] = useState<
    string | undefined
  >();
  const [isInitialized, setIsInitialized] = useState(false);
  const [extraLoginParams, setExtraLoginParams] = useState<
    ExtraLoginParams | undefined
  >();

  const modifiedAvo: ModifiedAvoType<AvoType> = useMemo(
    () =>
      mapValues(Avo, (value) => {
        if (typeof value === "function") {
          return (args: any) => {
            let updatedArgs = { ...args };
            if (typeof args === "object") {
              updatedArgs.epUserSubscriptionPlan = user?.package;
              updatedArgs.epUserIsTelegramConnected =
                user?.is_telegram_connected;
              updatedArgs.epUserIsDiscordConnected = user?.is_discord_connected;
              updatedArgs.epUserFeatureFlags = user?.feature_flags;
              updatedArgs.epUserSubscriptionIsActive = !user?.isExpired();
              updatedArgs.epUserIsAdmin = user?.is_admin;
              updatedArgs.epUserIsMobileAppUser = user?.is_mobile_app_user;
              updatedArgs.epUserSubscriptionIsActive = true;
              updatedArgs.epUserIsOnboarding = user?.is_onboarding;
              updatedArgs.epUserIsDarkMode = theme.isDarkMode;
              updatedArgs.isTelegramMiniApp = isTelegramWebApp;
            }
            // @ts-ignore
            value(updatedArgs);
          };
        }
        return value;
      }) as any,
    [user, theme.isDarkMode]
  );

  const getParsedExtraParams = () => {
    const filteredObject = pickBy(
      extraLoginParams,
      (_, key) => key in EXTRA_PARAMS_KEY_MAPPING
    );
    return mapKeys(
      filteredObject,
      (_, key) => EXTRA_PARAMS_KEY_MAPPING[key as keyof ExtraLoginParams]
    );
  };

  useEffect(() => {
    if (isInitialized && user) {
      const userId = user.id.toString();
      if (user.should_track && !initializedUserId) {
        modifiedAvo.userLoggedIn({
          userId_: userId,
          userSystemDarkModeOn,
          ...getParsedExtraParams(),
        });
        setExtraLoginParams(undefined);
        setInitializedUserId(userId);
      } else if (initializedUserId && initializedUserId !== userId) {
        onUserIdChange?.(userId, initializedUserId);
      }
    }
  }, [isInitialized, user?.id]);

  let avoDebugOptions = {};
  if (!enableLoading) {
    avoDebugOptions = {
      avoLogger: {
        logDebug: () => true,
        logError: () => true,
        logWarn: () => true,
      },
      webDebugger: false,
    };
  }

  return {
    initializedUserId,
    setInitializedUserId,
    isInitialized,
    setIsInitialized,
    setExtraLoginParams,
    modifiedAvo: isInitialized && initializedUserId ? modifiedAvo : undefined,
    avoDebugOptions,
  };
};

const EXTRA_PARAMS_KEY_MAPPING: Record<keyof ExtraLoginParams, string> = {
  source: "source",
  source_button: "sourceButton",
  is_new_user: "isNewUser",
};
